import { useMemo } from "react";
import { useExpanded, useTable, useRowSelect } from "react-table";
import { cleanObject, formatCurrency } from "../../../../../helpers";
import primaryComponents from "../../../../primaryComponents";
import { convertToTilteCase } from "../../../../../helpers";
import secondaryComponents from "../../..";
import { useSelector } from "react-redux";

export const useProductList = ({
  productList = [],
  formData = {},
  setFormData = () => {},
}) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const storeCurrency = useMemo(() => {
    return storeList?.find((item) => `${item?.id}` === `${formData?.store}`)
      ?.currency_code;
  }, [formData?.store]);
  // Derive select-all state directly from formData + productList
  const allPageKeys = useMemo(
    () =>
      productList.map(
        (p) => `${p.product_id || p.service_id}${p.variant_id || ""}`,
      ),
    [productList],
  );

  const isAllSelected =
    allPageKeys.length > 0 &&
    allPageKeys.every((key) => !!formData?.storefront_items?.[key]);

  const isIndeterminate =
    !isAllSelected &&
    allPageKeys.some((key) => !!formData?.storefront_items?.[key]);

  const handleSelectAll = (checked) => {
    setFormData((prev) => {
      const updatedItems = { ...prev.storefront_items };
      if (checked) {
        productList?.forEach((p) => {
          const key = `${p.product_id || p.service_id}${p.variant_id || ""}`;
          updatedItems[key] = cleanObject({
            service_id: p.service_id ?? null,
            product_id: p.product_id ?? null,
            variant_id: p.variant_id ?? null,
            pricing_type_id: 1,
          });
        });
      } else {
        allPageKeys.forEach((key) => delete updatedItems[key]);
      }
      return { ...prev, storefront_items: updatedItems };
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "id",
        accessor: "name",
        Header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <primaryComponents.Checkbox
              name="select-all"
              id="select-all"
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={handleSelectAll}
            />
            <span style={{ marginLeft: 10 }}>Product Name</span>
          </div>
        ),
        Cell: ({ cell: { value }, row }) => {
          const key = `${row?.original?.product_id || row?.original?.service_id}${row?.original?.variant_id || ""}`;
          const isChecked = !!formData?.storefront_items?.[key];

          const handleChange = (checked) => {
            setFormData((prev) => {
              const updatedItems = { ...prev.storefront_items };
              if (checked) {
                updatedItems[key] = cleanObject({
                  service_id: row?.original?.service_id ?? null,
                  product_id: row?.original?.product_id ?? null,
                  variant_id: row?.original?.variant_id ?? null,
                  pricing_type_id: 1,
                });
              } else {
                delete updatedItems[key];
              }
              return { ...prev, storefront_items: updatedItems };
            });
          };

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <primaryComponents.Checkbox
                id={key}
                isChecked={isChecked}
                onChange={handleChange}
              />
              <label htmlFor={key} style={{ cursor: "pointer" }}>
                <secondaryComponents.ConcatenateWithTooltip
                  value={value}
                  noOfCharacters={50}
                />
              </label>
            </div>
          );
        },
      },
      {
        Header: "Product Type",
        accessor: "is_service",
        Cell: ({ cell: { value } }) => (
          <span>{value ? "Service" : "Product"}</span>
        ),
      },
      {
        Header: "Quantity",
        accessor: "stock_quantity",
        Cell: ({ cell: { value, row } }) => {
          if (value !== null && value !== undefined)
            return (
              <span
                style={{
                  color:
                    value > row.original["minimum_stock_quantity"]
                      ? "#19AE57"
                      : "red",
                  backgroundColor:
                    value > row.original["minimum_stock_quantity"]
                      ? "#E6FFF0"
                      : "#FFEEE9",
                  borderRadius: "4px",
                  padding: "4px",
                  height: "26px",
                  fontSize: "12px",
                }}
              >
                {value}
              </span>
            );
          if (!value) return "N/A";
        },
      },
      {
        Header: "Selling Price",
        accessor: "selling_price",
        Cell: ({ cell: { value } }) =>
          formatCurrency({ value, currencyCode: storeCurrency }),
      },
      {
        Header: "Category",
        accessor: "category_name",
        Cell: ({ cell: { value } }) => convertToTilteCase(value),
      },
    ],
    [isAllSelected, isIndeterminate, formData, productList],
  );

  const instance = useTable(
    { columns, data: productList },
    useExpanded,
    useRowSelect,
  );

  return { instance };
};
